/**
 * The tenant security rows: a table on desktop, cards below 768px.
 *
 * ONE COPY IN THE DOM, NOT TWO
 * ----------------------------
 * Which layout renders is decided in JavaScript by `useIsMobile` — the repo's
 * locked 768px hook — rather than by a `hidden md:block` / `md:hidden` pair.
 * Both look right in a browser, but the CSS version leaves every tenant in the
 * document twice, and anything walking the DOM without a stylesheet (a test, a
 * crawler, a screen reader in browse mode) then sees each row doubled with no
 * way to tell which copy is real. `ResponsibilityAreaList` records the same
 * decision and the same reason.
 *
 * NOTHING IS FILTERED AWAY BY DEFAULT
 * -----------------------------------
 * Returned and excluded rows are here from the start. They answer "why is this
 * deposit not in the total?" — the single most likely question this page has to
 * field — and hiding them would convert it into a support request. The status
 * filter narrows; it does not decide what deserves to exist.
 */
import React from "react";
import { ChevronRight } from "lucide-react";

import { chf, chfPrecise } from "../../../components/admin/companyFinance/financeFormat";
import { useIsMobile } from "../../../hooks/useIsMobile";
import DepositBadge from "./DepositBadge";
import {
  depositTypeLabel,
  providerLabel,
  depositSecurityState,
} from "./depositsLabels";

const HEAD =
  "px-2.5 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground";
const CELL = "px-2.5 py-2.5 align-top text-[12.5px] text-foreground";

/** The unit and room of a tenancy, as a two-line cell. */
function objectLabel(item) {
  return String(item.short_unit_id || item.unit_title || "").trim() || "—";
}

function StateBadge({ item }) {
  // Both halves go to the shared helper: the state alone cannot say whether
  // "held" means a payment received or a deposit lodged.
  const { label, tone } = depositSecurityState(
    item.security_state,
    item.deposit_type
  );
  return <DepositBadge tone={tone}>{label}</DepositBadge>;
}

/**
 * The tenant's own insurance premium, shown under the provider and nowhere else.
 *
 * It is what the TENANT pays their insurer — not our expense, not part of any
 * security total, and not the landlord premium the KPI row carries. It earns a
 * place in the row because it explains the policy; it earns no place above.
 */
function PremiumHint({ item }) {
  const premium = Number(item.annual_premium_chf);
  if (String(item.deposit_type || "").toLowerCase() !== "insurance") return null;
  if (!(premium > 0)) return null;
  return (
    <span className="mt-0.5 block text-[11px] text-muted-foreground">
      {`Prämie p.a.: ${chfPrecise(premium)}`}
    </span>
  );
}

/**
 * K4c.2: this was a `<Link>` to /admin/tenants/{id}. It is now the trigger for
 * the deposit detail modal, and the row has ONE action rather than two.
 *
 * The navigation was not dropped: the same `<Link>` lives inside the modal as
 * "Mieter öffnen", so middle-click, "open in new tab" and copy-link still
 * reach the tenant page — one click further along. Putting both in the row
 * would have meant two chevrons competing for the same word.
 *
 * A button, not a link, because it opens a dialog and goes nowhere; the
 * aria-label says so rather than promising "öffnen".
 */
function DetailAction({ item, onOpenDetails, className }) {
  if (!onOpenDetails) return null;
  const name = item.tenant_name || "Mieter";
  return (
    <button
      type="button"
      onClick={() => onOpenDetails(item)}
      aria-label={`Kaution von ${name} anzeigen`}
      data-testid={`deposits-tenant-details-${item.tenancy_id}`}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className || ""}`}
    >
      <span className="sr-only md:not-sr-only">Details</span>
      <ChevronRight aria-hidden className="h-3.5 w-3.5" />
    </button>
  );
}

function DesktopTable({ items, onOpenDetails }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">Mieterkautionen</caption>
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className={HEAD}>Mieter</th>
            <th scope="col" className={HEAD}>Mietverhältnis</th>
            <th scope="col" className={HEAD}>Objekt / Zimmer</th>
            <th scope="col" className={HEAD}>Kautionsart</th>
            <th scope="col" className={HEAD}>Anbieter</th>
            <th scope="col" className={`${HEAD} text-right`}>Betrag</th>
            <th scope="col" className={HEAD}>Status</th>
            <th scope="col" className={`${HEAD} text-right`}>
              <span className="sr-only">Aktion</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.tenancy_id}
              data-testid={`deposits-tenant-row-${item.tenancy_id}`}
              className="border-b border-border/60 last:border-0 hover:bg-muted/40"
            >
              <td className={`${CELL} font-medium`}>{item.tenant_name || "—"}</td>
              <td className={`${CELL} text-muted-foreground`}>
                {item.reference_number || "—"}
              </td>
              <td className={CELL}>
                {objectLabel(item)}
                {item.room_name ? (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {item.room_name}
                  </span>
                ) : null}
              </td>
              <td className={CELL}>{depositTypeLabel(item.deposit_type)}</td>
              <td className={CELL}>
                {providerLabel(item.provider, item.deposit_type)}
                <PremiumHint item={item} />
              </td>
              <td className={`${CELL} text-right tabular-nums`}>
                {chf(item.effective_amount_chf)}
              </td>
              <td className={CELL}>
                <StateBadge item={item} />
              </td>
              <td className={`${CELL} text-right`}>
                <DetailAction item={item} onOpenDetails={onOpenDetails} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ items, onOpenDetails }) {
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {items.map((item) => (
        <li
          key={item.tenancy_id}
          data-testid={`deposits-tenant-row-${item.tenancy_id}`}
          className="rounded-lg border border-border p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="m-0 text-[13px] font-medium text-foreground">
                {item.tenant_name || "—"}
              </p>
              <p className="m-0 text-[11px] text-muted-foreground">
                {item.reference_number || "—"}
              </p>
            </div>
            <StateBadge item={item} />
          </div>

          <p className="mt-2 text-[12px] text-foreground">
            {objectLabel(item)}
            {item.room_name ? (
              <span className="text-muted-foreground">{` · ${item.room_name}`}</span>
            ) : null}
          </p>

          <div className="mt-2 flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="m-0 text-[12px] text-foreground">
                {depositTypeLabel(item.deposit_type)}
                <span className="text-muted-foreground">
                  {` · ${providerLabel(item.provider, item.deposit_type)}`}
                </span>
              </p>
              <PremiumHint item={item} />
            </div>
            <span className="shrink-0 text-[13px] font-semibold tabular-nums text-foreground">
              {chf(item.effective_amount_chf)}
            </span>
          </div>

          <div className="mt-2 flex justify-end">
            <DetailAction item={item} onOpenDetails={onOpenDetails} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function TenantDepositsTable({ items, onOpenDetails }) {
  const isMobile = useIsMobile();
  if (!items.length) return null;
  return isMobile ? (
    <MobileCards items={items} onOpenDetails={onOpenDetails} />
  ) : (
    <DesktopTable items={items} onOpenDetails={onOpenDetails} />
  );
}
